/* Favori Keukens — header, menu, reveal, lightbox, video. No dependencies. */
(() => {
  const root = document.documentElement;

  /* ---------- Header: scrolled state via sentinel (no scroll listener) ---------- */
  function initHeader() {
    const header = document.querySelector('[data-fk-header]');
    if (!header || header.dataset.ready) return;
    header.dataset.ready = '1';

    let sentinel = document.getElementById('fk-top-sentinel');
    if (!sentinel) {
      sentinel = document.createElement('div');
      sentinel.id = 'fk-top-sentinel';
      sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:80px;pointer-events:none;';
      document.body.prepend(sentinel);
    }
    new IntersectionObserver(([entry]) => {
      header.classList.toggle('is-scrolled', !entry.isIntersecting);
    }).observe(sentinel);

    const burger = header.querySelector('[data-fk-burger]');
    const overlay = header.querySelector('[data-fk-overlay]');
    if (!burger || !overlay) return;

    const setOpen = (open) => {
      root.classList.toggle('fk-menu-open', open);
      burger.setAttribute('aria-expanded', String(open));
      overlay.toggleAttribute('inert', !open);
      if (open) overlay.querySelector('a')?.focus({ preventScroll: true });
    };

    overlay.setAttribute('inert', '');
    burger.addEventListener('click', () => setOpen(!root.classList.contains('fk-menu-open')));
    overlay.addEventListener('click', (e) => {
      if (e.target.closest('a')) setOpen(false);
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && root.classList.contains('fk-menu-open')) {
        setOpen(false);
        burger.focus();
      }
    });
    window.matchMedia('(min-width: 1200px)').addEventListener('change', (e) => {
      if (e.matches) setOpen(false);
    });
  }

  /* ---------- Reveal on scroll ---------- */
  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                entry.target.classList.add('is-in');
                revealObserver.unobserve(entry.target);
              }
            });
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
        )
      : null;

  function initReveal(scope = document) {
    scope.querySelectorAll('[data-fk-reveal]:not(.is-in)').forEach((el) => {
      if (revealObserver) revealObserver.observe(el);
      else el.classList.add('is-in');
    });
  }

  /* ---------- Gallery lightbox ---------- */
  let lightbox;
  function getLightbox() {
    if (lightbox) return lightbox;
    lightbox = document.createElement('dialog');
    lightbox.className = 'fk-lightbox';
    lightbox.setAttribute('aria-label', 'Afbeelding vergroot');
    lightbox.innerHTML = `
      <img alt="">
      <button type="button" class="fk-lightbox__btn fk-lightbox__close" aria-label="Sluiten"><svg class="fk-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round"><path d="M6 6l12 12M18 6 6 18"/></svg></button>
      <button type="button" class="fk-lightbox__btn fk-lightbox__prev" aria-label="Vorige"><svg class="fk-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5m6-6-6 6 6 6"/></svg></button>
      <button type="button" class="fk-lightbox__btn fk-lightbox__next" aria-label="Volgende"><svg class="fk-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14m-6-6 6 6-6 6"/></svg></button>`;
    document.body.appendChild(lightbox);

    const img = lightbox.querySelector('img');
    let items = [];
    let index = 0;
    const show = (i) => {
      index = (i + items.length) % items.length;
      img.src = items[index].dataset.full;
      img.alt = items[index].dataset.alt || '';
    };
    lightbox.open_ = (list, i) => {
      items = list;
      show(i);
      lightbox.querySelector('.fk-lightbox__prev').hidden = items.length < 2;
      lightbox.querySelector('.fk-lightbox__next').hidden = items.length < 2;
      lightbox.showModal();
    };
    lightbox.querySelector('.fk-lightbox__close').addEventListener('click', () => lightbox.close());
    lightbox.querySelector('.fk-lightbox__prev').addEventListener('click', () => show(index - 1));
    lightbox.querySelector('.fk-lightbox__next').addEventListener('click', () => show(index + 1));
    lightbox.addEventListener('click', (e) => {
      if (e.target === lightbox) lightbox.close();
    });
    lightbox.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') show(index - 1);
      if (e.key === 'ArrowRight') show(index + 1);
    });
    return lightbox;
  }

  function initGalleries(scope = document) {
    scope.querySelectorAll('[data-fk-lightbox]').forEach((gallery) => {
      if (gallery.dataset.ready) return;
      gallery.dataset.ready = '1';
      gallery.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-full]');
        if (!btn) return;
        const list = [...gallery.querySelectorAll('[data-full]')];
        getLightbox().open_(list, list.indexOf(btn));
      });
    });
  }

  /* ---------- Video play/pause + pause when off-screen ---------- */
  function initVideos(scope = document) {
    scope.querySelectorAll('[data-fk-video]').forEach((wrap) => {
      if (wrap.dataset.ready) return;
      wrap.dataset.ready = '1';
      const video = wrap.querySelector('video');
      const toggle = wrap.querySelector('[data-fk-video-toggle]');
      if (!video) return;
      let userPaused = false;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        userPaused = true;
        video.pause();
      }
      const sync = () => wrap.classList.toggle('is-paused', video.paused);
      video.addEventListener('play', sync);
      video.addEventListener('pause', sync);
      sync();
      toggle?.addEventListener('click', () => {
        if (video.paused) {
          userPaused = false;
          video.play();
        } else {
          userPaused = true;
          video.pause();
        }
      });
      if (video.autoplay && 'IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting && !userPaused) video.play().catch(() => {});
          else if (!entry.isIntersecting) video.pause();
        }).observe(video);
      }
    });
  }

  function init(scope) {
    initHeader();
    initReveal(scope);
    initGalleries(scope);
    initVideos(scope);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (e) => {
    const header = e.target.querySelector('[data-fk-header]');
    if (header) root.classList.remove('fk-menu-open');
    init(e.target);
  });
})();
